import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag } from '../ui';
import { FileText, Wallet, Minus } from 'lucide-react';
import { formatIst } from '../lib/time';
import { CreditNoteDrawer } from './CreditNoteDrawer';

const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPE_META: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  INVOICE: { label: 'Invoice', cls: 'bg-danger-500/10 text-danger-600', icon: <FileText size={16} /> },
  PAYMENT: { label: 'Payment', cls: 'bg-success-500/10 text-success-600', icon: <Wallet size={16} /> },
  CREDIT_NOTE: { label: 'Credit note', cls: 'bg-sky-500/10 text-sky-600', icon: <Minus size={16} /> },
  DEBIT_NOTE: { label: 'Debit note', cls: 'bg-warning-500/10 text-warning-600', icon: <FileText size={16} /> },
  CREDIT_APPLIED: { label: 'Credit used', cls: 'bg-brand-600/10 text-brand-700', icon: <Wallet size={16} /> },
  DISCOUNT: { label: 'Discount', cls: 'bg-brand-600/10 text-brand-700', icon: <Minus size={16} /> },
  OPENING: { label: 'Opening', cls: 'bg-surface-3 text-subtle', icon: <Minus size={16} /> },
};

/**
 * Retailer statement-of-account table (shared by the retailer's own My Account and the staff Retailer detail).
 * Enriched ledger: each row's source document (invoice / payment / credit-note no) deep-links (a credit note
 * opens the canonical note drawer), payment mode is shown, and the balance uses Dr/Cr.
 *
 * `perspective` mirrors the frame for the RETAILER's own statement (reciprocal ledger): the stored ledger is
 * Green Bay's frame (positive = the retailer owes = Dr). Staff see that as-is. The retailer sees it in plain
 * language from THEIR side — a positive balance reads "payable" (they owe), a negative one "in credit" (advance
 * in their favour) — and the Debit/Credit columns are relabelled to "Charged"/"Paid or credited". Same numbers,
 * no double-entry jargon, no negative signs — just their side of the same ledger.
 */
export function LedgerStatement({ data, loading, pageSize = 25, perspective = 'seller' }: { data: any[]; loading?: boolean; pageSize?: number; perspective?: 'seller' | 'retailer' }) {
  const navigate = useNavigate();
  const [noteId, setNoteId] = useState<string | null>(null);
  const retailerView = perspective === 'retailer';
  return (
    <>
      <Table
        rowKey="id"
        dataSource={data}
        loading={loading}
        pagination={{ pageSize, hideOnSinglePage: true }}
        size="small"
        scroll={{ x: 'max-content' }}
        columns={[
          { title: 'Date', dataIndex: 'createdAt', width: 170, render: (v) => formatIst(v) },
          {
            title: 'Type', dataIndex: 'txnType', width: 130,
            render: (v) => {
              const m = TYPE_META[v] ?? { label: v, cls: 'bg-surface-3 text-subtle', icon: null };
              return <span className={`inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-[11px] font-medium ${m.cls}`}>{m.icon}{m.label}</span>;
            },
          },
          {
            title: 'Details',
            render: (_: any, e: any) => (
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  {e.docNo
                    ? (e.creditNoteId
                        ? <button type="button" onClick={() => setNoteId(String(e.creditNoteId))} className="font-medium text-brand-700 hover:underline dark:text-brand-400">{e.docNo}</button>
                        : e.orderId
                          ? <button type="button" onClick={() => navigate(`/orders/${e.orderId}`)} className="font-medium text-brand-700 hover:underline">{e.docNo}</button>
                          : <span className="font-medium text-content">{e.docNo}</span>)
                    : <span className="text-content">{e.narration ?? '—'}</span>}
                  {e.mode && <Tag style={{ margin: 0 }}>{e.mode}</Tag>}
                  {e.isMemo && <span className="text-[12px] font-semibold text-brand-700 dark:text-brand-400">{inr2(e.creditAmount)}</span>}
                  {e.noteStatus === 'CANCELLED' && <span className="rounded-pill bg-danger-500/10 px-1.5 py-0.5 text-[10px] font-medium text-danger-600">reversed</span>}
                  {e.orderNo && !e.isMemo && <span className="text-[12px] text-muted">· {e.orderNo}</span>}
                </div>
                {e.docNo && e.narration && <div className="text-[12px] text-muted">{e.narration}</div>}
              </div>
            ),
          },
          { title: retailerView ? 'Charged' : 'Debit', dataIndex: 'debit', align: 'right' as const, width: 110, render: (v) => Number(v) > 0 ? <span className="text-danger-600">{inr2(v)}</span> : <span className="text-subtle">—</span> },
          { title: retailerView ? 'Paid / credited' : 'Credit', dataIndex: 'credit', align: 'right' as const, width: 120, render: (v) => Number(v) > 0 ? <span className="text-success-600">{inr2(v)}</span> : <span className="text-subtle">—</span> },
          {
            title: 'Balance', dataIndex: 'balance', align: 'right' as const, width: 150,
            render: (v, e: any) => {
              if (e.isMemo) return <span className="text-subtle">—</span>; // a credit application doesn't move the AR balance
              const n = Number(v);
              const owes = n > 0.01, credit = n < -0.01;
              // Seller frame = Dr/Cr; retailer frame = plain "payable"/"in credit" (their side of the mirror).
              const suffix = retailerView ? (owes ? 'payable' : credit ? 'in credit' : '') : (owes ? 'Dr' : credit ? 'Cr' : '');
              return (
                <span className={`font-bold ${owes ? 'text-danger-600' : credit ? 'text-success-600' : 'text-content'}`}>
                  {inr2(Math.abs(n))}{suffix ? <span className="ml-1 text-[11px] font-normal">{suffix}</span> : ''}
                </span>
              );
            },
          },
        ]}
      />
      <CreditNoteDrawer id={noteId} onClose={() => setNoteId(null)} />
    </>
  );
}
